import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { SecureStorageService } from '../../../core/services/secure-storage.service';

/**
 * The Notifications screen, on the gateway.
 *
 * Every method here talks to the gateway: a contest going live, a winner being drawn, the five
 * participant reminders and the client announcements are all written to the same database this
 * screen lists.
 *
 * The action codes:
 *   8185 send                    8190 mark one read
 *   8186 send to a contest       8191 mark the named ones read
 *   8187 the log list            8192 clear the admin bell
 *   8188 one tab, paged          8193 delete
 *   8189 the bell's count        8194 the contest dropdown
 *
 * The response shapes are unchanged — the same field names, the same paging envelope, the same
 * "No any user found" — so the components and templates need no edits.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  constructor(
    private http: HttpClient,
    // The gateway takes the session token on the Authorization header, and it is held in
    // secure storage — not localStorage — so the header cannot be built without this.
    private secureStorage: SecureStorageService
  ) {}

  private readonly gatewayUrl = environment.loyaltyApiUrl;

  private gatewayHeaders(skipLoader = false): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.secureStorage.getItem('authToken') || ''}`,
    });
    if (skipLoader) headers = headers.set('X-Skip-Loader', 'true');
    return headers;
  }

  private callAction<T = any>(
    actioncode: number,
    data: Record<string, any> = {},
    skipLoader = false
  ): Observable<T> {
    return this.http
      .post<any>(this.gatewayUrl, { actioncode, data }, { headers: this.gatewayHeaders(skipLoader) })
      .pipe(
        map((res) => {
          if (!res || res.isSuccess === false) {
            const message = res?.message || 'Request failed.';
            throw { message, error: { message, code: res?.code }, status: 400 };
          }
          return res.data as T;
        }),
        catchError((err) => throwError(() => err))
      );
  }

  // ── the two sends ───────────────────────────────────────────────────────────
  // The payload is passed through as the composer builds it: send_to, the segment, the id
  // lists, both languages, the attachments. The server resolves the audience and refuses an
  // empty one, so "sent" on this screen now means somebody was actually addressed.
  addNotification(notification: any): Observable<any> {
    return this.callAction<any>(8185, notification).pipe(
      catchError((error) =>
        throwError(
          () =>
            new Error(
              error.error?.message ||
                error.message ||
                'Failed to create notification. Please try again.'
            )
        )
      )
    );
  }

  addCustomNotification(notification: any): Observable<any> {
    return this.callAction<any>(8186, notification).pipe(
      catchError((error) =>
        throwError(
          () =>
            new Error(
              error.error?.message ||
                error.message ||
                'Failed to create notification. Please try again.'
            )
        )
      )
    );
  }

  // ── the lists ───────────────────────────────────────────────────────────────
  getNotificationHistory(
    skipLoaderOrParams: boolean | any = false,
    params?: any
  ): Observable<any> {
    const skipLoader =
      typeof skipLoaderOrParams === 'boolean' ? skipLoaderOrParams : false;
    const queryParams =
      typeof skipLoaderOrParams === 'boolean' ? params : skipLoaderOrParams;

    return this.callAction<any>(8187, queryParams || {}, skipLoader).pipe(
      catchError((error) =>
        throwError(
          () => new Error(error.error?.message || 'Failed to load notification history.')
        )
      )
    );
  }

  // The three tabs — welcome / participants / create_by_admin — are notification categories,
  // and the paging is done in SQL now instead of in the browser over the whole table.
  getNotificationsByType(notificationType: string, params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(
      8188,
      { notification_type: notificationType, ...(params || {}) },
      skipLoader
    ).pipe(
      catchError((error) =>
        throwError(() => new Error(error.error?.message || 'Failed to load notifications.'))
      )
    );
  }

  getNotificationLogs(params?: any, skipLoader = false): Observable<any> {
    return this.callAction<any>(8187, params || {}, skipLoader).pipe(
      catchError((error) =>
        throwError(
          () => new Error(error.error?.message || 'Failed to load notification request.')
        )
      )
    );
  }

  getNotificationUnread(skipLoader = false): Observable<any> {
    return this.callAction<any>(8189, {}, skipLoader).pipe(
      catchError((error) =>
        throwError(
          () =>
            new Error(
              error.error?.message || 'Failed to load unread notification count.'
            )
        )
      )
    );
  }

  // ── one row ─────────────────────────────────────────────────────────────────
  // No gateway code: editing a notification that has already been delivered would rewrite what
  // people were told after they were told it, and the delivery rows are kept as sent. The
  // composer's edit path therefore reports that it is not available.
  editNotification(id: number, notificationData: any): Observable<any> {
    void id;
    void notificationData;
    return throwError(
      () => new Error('A sent notification cannot be edited. Create a new notification instead.')
    );
  }

  // A soft delete: it leaves the screen, and the record of who was told stays.
  deleteNotification(notificationId: number | string): Observable<void> {
    return this.callAction<void>(8193, { id: notificationId }).pipe(
      catchError((error) =>
        throwError(() => new Error(error.error?.message || 'Failed to delete notification.'))
      )
    );
  }

  // ── read state ──────────────────────────────────────────────────────────────
  markAsRead(payload: any): Observable<any> {
    return this.callAction<any>(8190, payload).pipe(
      catchError((error) =>
        throwError(
          () => new Error(error.error?.message || 'Failed to mark notification as read.')
        )
      )
    );
  }

  markAllAsRead(payload: { notification_ids: (number | string)[] }): Observable<any> {
    return this.callAction<any>(8191, payload).pipe(
      catchError((error) =>
        throwError(
          () => new Error(error.error?.message || 'Failed to mark all notifications as read.')
        )
      )
    );
  }

  markAllAsReadAdmin(payload: { notification_ids: (number | string)[] }): Observable<any> {
    return this.callAction<any>(8192, payload).pipe(
      catchError((error) =>
        throwError(
          () => new Error(error.error?.message || 'Failed to mark all notifications as read.')
        )
      )
    );
  }

  getContestLookup(): Observable<any> {
    return this.callAction<any>(8194).pipe(
      catchError((error) =>
        throwError(
          () =>
            new Error(error.error?.message || error.message || 'Failed to get campaign lookup.')
        )
      )
    );
  }
}
